import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { translations } from '../../i18n';
import './landing.css';

export const Landing = () => {
  const navigate = useNavigate();
  const [isDark, setIsDark] = useState(() => localStorage.getItem('theme') !== 'light');
  const [lang, setLang] = useState<'ES' | 'EN'>(() => (localStorage.getItem('lang') as 'ES' | 'EN') || 'ES');
  const [scrolled, setScrolled] = useState(false);
  const [cookiesAccepted, setCookiesAccepted] = useState(() => 
    localStorage.getItem('cookiesAccepted') === 'true'
  );

  const acceptCookies = () => {
    localStorage.setItem('cookiesAccepted', 'true');
    setCookiesAccepted(true);
  };

  const rejectCookies = () => {
    localStorage.setItem('cookiesAccepted', 'false');
    setCookiesAccepted(true); // hide banner either way
  };
  const l = translations[lang].landing;

  useEffect(() => {
    document.body.classList.toggle('light-mode', !isDark);
  }, [isDark]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const toggleTheme = () => {
    const next = !isDark;
    setIsDark(next);
    localStorage.setItem('theme', next ? 'dark' : 'light');
  };

  const changeLang = (val: 'ES' | 'EN') => {
    setLang(val);
    localStorage.setItem('lang', val);
  };

  const useCases = [
    {
      img: 'https://images.unsplash.com/photo-1503951914875-452162b0f3f1?w=400&h=500&fit=crop&crop=face',
      role: 'Barbero',
      name: 'Carlos M.',
      msg: '¡Tu turno en 10 min! ✂️',
      time: '2:48 PM',
      tag: 'Barbería',
      color: '#7c3aed',
    },
    {
      img: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=400&h=500&fit=crop&crop=face',
      role: 'Doctora',
      name: 'Dra. Pérez',
      msg: 'Su cita es mañana a las 9am 🏥',
      time: '4:15 PM',
      tag: 'Medicina',
      color: '#0ea5e9',
    },
    {
      img: 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=400&h=500&fit=crop&crop=face',
      role: 'Estilista',
      name: 'María S.',
      msg: 'Lista para tu cambio de look 💇‍♀️',
      time: '11:30 AM',
      tag: 'Salón',
      color: '#f59e0b',
    },
  ];

  return (
    <div className="lx-root">

      {/* ── NAVBAR ── */}
      <nav className={`lx-nav ${scrolled ? 'lx-nav--scrolled' : ''}`}>
        <div className="lx-nav__logo">
          <span className="lx-logo-text">TolkX</span>
        </div>
        <div className="lx-nav__actions">
          <button className="lx-icon-btn" onClick={toggleTheme}>
            {isDark ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="5"/>
                <line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
              </svg>
            )}
          </button>
          <select className="lx-lang-select" value={lang} onChange={e => changeLang(e.target.value as 'ES' | 'EN')}>
            <option value="ES">ES</option>
            <option value="EN">EN</option>
          </select>
          <button className="lx-btn lx-btn--ghost" onClick={() => navigate('/login')}>{l.login}</button>
          <button className="lx-btn lx-btn--primary" onClick={() => navigate('/register')}>{l.signup}</button>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className="lx-hero">
        <div className="lx-hero__content">
          <div className="lx-badge">Beta · Disponible ahora</div>
          <h1 className="lx-hero__headline">
            Chatea, agenda<br />
            <em>y cierra tratos.</em>
          </h1>
          <p className="lx-hero__sub">
            La app que conecta profesionales con sus clientes. Mensajes instantáneos, videollamadas y gestión de citas — todo en un solo lugar.
          </p>
          <div className="lx-hero__btns">
            <button className="lx-btn lx-btn--primary lx-btn--lg" onClick={() => navigate('/register')}>
              {l.signup} →
            </button>
            <button className="lx-btn lx-btn--ghost lx-btn--lg" onClick={() => navigate('/login')}>
              {l.login}
            </button>
          </div>
          <div className="lx-hero__trust">
            <div className="lx-trust__avatars">
              {['?img=10','?img=11','?img=12'].map((q,i) => (
                <img key={i} src={`https://i.pravatar.cc/40${q}`} alt="" className="lx-trust__av" />
              ))}
            </div>
            <span className="lx-trust__text">Únete a los primeros usuarios de TolkX</span>
          </div>
        </div>

        {/* ── USE CASE CARDS ── */}
        <div className="lx-hero__cards">
          {useCases.map((u, i) => (
            <div key={i} className="lx-uc-card" style={{ '--uc-color': u.color, animationDelay: `${i * 0.15}s` } as any}>
              <div className="lx-uc-card__img-wrap">
                <img src={u.img} alt={u.role} className="lx-uc-card__img" />
                <span className="lx-uc-card__tag" style={{ background: u.color }}>{u.tag}</span>
              </div>
              <div className="lx-uc-card__bubble">
                <div className="lx-uc-card__av" style={{ background: u.color }}>
                  {u.name.slice(0,2).toUpperCase()}
                </div>
                <div className="lx-uc-card__content">
                  <div className="lx-uc-card__name">{u.name}</div>
                  <div className="lx-uc-card__msg">{u.msg}</div>
                </div>
                <div className="lx-uc-card__time">{u.time}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="lx-how">
        <div className="lx-how__inner">
          <div className="lx-section-label">Cómo funciona</div>
          <h2 className="lx-section-title">Tu negocio, más conectado que nunca</h2>
          <div className="lx-steps">
            {[
              { n: '01', title: 'Crea tu perfil de negocio', desc: 'Registra tu barbería, clínica o salón en minutos.' },
              { n: '02', title: 'Conecta con tus clientes', desc: 'Ellos te encuentran, te escriben y reservan desde TolkX.' },
              { n: '03', title: 'Gestiona todo en un lugar', desc: 'Chats, citas, recordatorios — sin apps extra.' },
            ].map((s, i) => (
              <div key={i} className="lx-step">
                <div className="lx-step__num">{s.n}</div>
                <div className="lx-step__body">
                  <h3 className="lx-step__title">{s.title}</h3>
                  <p className="lx-step__desc">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section className="lx-features">
        <div className="lx-features__label">Por qué TolkX</div>
        {[
          { icon: '💬', title: l.feature1Title, desc: l.feature1Desc },
          { icon: '📞', title: l.feature2Title, desc: l.feature2Desc },
          { icon: '🔒', title: l.feature3Title, desc: l.feature3Desc },
          { icon: '📅', title: 'Citas integradas', desc: 'Agenda reuniones directamente desde el chat, sin salir de la conversación.' },
          { icon: '📊', title: 'Encuestas y archivos', desc: 'Comparte documentos, fotos y crea encuestas para tomar decisiones rápido.' },
          { icon: '🌐', title: 'Multiidioma', desc: 'Disponible en español e inglés. Más idiomas próximamente.' },
        ].map((f) => (
          <div className="lx-card" key={f.title}>
            <span className="lx-card__icon">{f.icon}</span>
            <h3 className="lx-card__title">{f.title}</h3>
            <p className="lx-card__desc">{f.desc}</p>
          </div>
        ))}
      </section>

      {/* ── STATS ── */}
      <div className="lx-stats">
        <div className="lx-stat">
          <span className="lx-stat__num">100%</span>
          <span className="lx-stat__label">Gratis para siempre</span>
        </div>
        <div className="lx-stat">
          <span className="lx-stat__num">0ms</span>
          <span className="lx-stat__label">Latencia en tiempo real</span>
        </div>
        <div className="lx-stat">
          <span className="lx-stat__num">E2E</span>
          <span className="lx-stat__label">Cifrado extremo a extremo</span>
        </div>
      </div>

      {/* ── CTA ── */}
      <section className="lx-cta">
        <div className="lx-cta__inner">
          <h2 className="lx-cta__text">{l.cta}</h2>
          <p className="lx-cta__sub">Únete gratis. Sin tarjeta de crédito. Sin complicaciones.</p>
          <button className="lx-btn lx-btn--primary lx-btn--lg" onClick={() => navigate('/register')}>
            Crear cuenta gratis →
          </button>
        </div>
      </section>

      <footer className="lx-footer">
        <span>{l.footer}</span>
        <span>Hecho con ♥ en República Dominicana · TolkX {new Date().getFullYear()}</span>
      </footer>

      {/* ── COOKIE BANNER ── */}
      {!cookiesAccepted && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9999,
          background: 'rgba(15,15,25,0.97)',
          backdropFilter: 'blur(12px)',
          borderTop: '1px solid rgba(124,58,237,0.3)',
          padding: '1rem 1.5rem',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: '1rem', flexWrap: 'wrap',
        }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#e2e2f0', lineHeight: 1.5 }}>
              🍪 Usamos cookies esenciales para el funcionamiento de la app (sesión, preferencias y notificaciones).
              {' '}<span style={{ color: '#a78bfa', cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => window.open('/privacy', '_blank')}>
                Ver política de privacidad
              </span>
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
            <button onClick={rejectCookies} style={{
              background: 'transparent', border: '1px solid rgba(255,255,255,0.2)',
              color: '#e2e2f0', borderRadius: '8px', padding: '0.4rem 1rem',
              cursor: 'pointer', fontSize: '0.82rem',
            }}>
              Rechazar
            </button>
            <button onClick={acceptCookies} style={{
              background: '#7c3aed', border: 'none',
              color: '#fff', borderRadius: '8px', padding: '0.4rem 1.2rem',
              cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600,
            }}>
              Aceptar
            </button>
          </div>
        </div>
      )}

    </div>
  );
};